// shared/API.md §47 — report a message, block a person (plugin `com.satoritech.moderation`).
//
// Twins: ios/Sources/ViewModels/Moderation.swift, android viewmodels/Moderation.kt,
// windows Logic/Moderation.cs — same keys, same rules, same tests.

export const SYNC_STATE_KEY = 'satori_blocked_users';
export const BLOCKS_CHANGED_EVENT = 'custom_com.satoritech.moderation_blocks_changed';
export const PLUGIN_BASE = '/plugins/com.satoritech.moderation/api/v1';

/** The report reasons, in the order the sheet lists them (wire keys — never localise). */
export const REASONS = ['spam', 'harassment', 'inappropriate', 'other'] as const;
export type ReportReason = (typeof REASONS)[number];

/** The i18n key of a reason's label. */
export function reasonLabelKey(reason: ReportReason): string {
  return `moderation.reason.${reason}`;
}

/** A user id as the wire carries it (Mattermost's are 26 characters; fixtures use short ones). */
const ID = /^[A-Za-z0-9_-]{1,64}$/;

/** Ids from a wire answer or a stored value: valid ids only, unique, sorted. Garbage → []. */
export function parseBlocked(raw: unknown): string[] {
  const list = Array.isArray(raw) ? raw : raw && typeof raw === 'object' && Array.isArray((raw as { blocked?: unknown }).blocked) ? (raw as { blocked: unknown[] }).blocked : null;
  if (!list) return [];
  return [...new Set(list.filter((v): v is string => typeof v === 'string' && ID.test(v)))].sort();
}

/** The sync_state value (a JSON array; '' and garbage decode to the empty list). */
export function decode(value: string | null | undefined): Set<string> {
  if (!value) return new Set();
  try {
    return new Set(parseBlocked(JSON.parse(value)));
  } catch {
    return new Set();
  }
}

export function encode(ids: Iterable<string>): string {
  return JSON.stringify(parseBlocked([...ids]));
}

/**
 * «Reportar mensaje» is offered on a synced, non-system post by someone else (the reply rule:
 * a pending or failed post has no server id the plugin could find).
 */
export function canReport(post: { userId: string; type: string; synced: boolean }, me: string | null | undefined): boolean {
  if (!post.synced || !me || post.userId === '' || post.userId === me) return false;
  return !post.type.startsWith('system_');
}

/** The posts a block hides: every post (root or reply) whose author is blocked. */
export function visiblePosts<T extends { userId: string }>(posts: T[], blocked: Set<string>): T[] {
  if (blocked.size === 0) return posts;
  return posts.filter((p) => !blocked.has(p.userId));
}

/** A direct conversation with a blocked person leaves the channel list (and its counts with it). */
export function hidesDirect(channel: { type: string; name: string }, me: string, blocked: Set<string>): boolean {
  if (channel.type !== 'D' || blocked.size === 0) return false;
  const parts = channel.name.split('__');
  if (parts.length !== 2) return false;
  const other = parts[0] === me ? parts[1] : parts[0];
  return other !== me && blocked.has(other);
}
